import { useState } from 'react';
import './InstitutionsPage.css';

const InstitutionsPage = () => {
  const [activeTab, setActiveTab] = useState('schools');

  const tabContent = {
    schools: {
      title: 'Schools with Class 11 & 12',
      features: [
        'Career guidance integrated into academic calendar',
        'Parent engagement tools and reports',
        'Stream-specific pathway mapping',
        'College application timeline management'
      ]
    },
    colleges: {
      title: 'PU / Junior Colleges',
      features: [
        'Comprehensive entrance exam preparation tracking',
        'College admission counselling support',
        'Student progress monitoring dashboards',
        'Batch-wise career analytics'
      ]
    },
    coaching: {
      title: 'Coaching Institutions',
      features: [
        'Competitive exam pathway guidance',
        'Performance-based college recommendations',
        'Mock test integration capabilities',
        'Parent communication portal'
      ]
    },
    trusts: {
      title: 'Educational Trusts & Networks',
      features: [
        'Multi-institution deployment',
        'Centralized admin controls',
        'Cross-campus analytics',
        'Unified branding across all institutions'
      ]
    }
  };

  return (
    <div className="institutions-page">
      {/* Hero Section - Light Theme */}
      <section className="inst-hero">
        <div className="container">
          <div className="inst-hero-grid">
            <div className="inst-hero-content">
              <span className="inst-hero-badge">For Educational Institutions</span>
              <h1 className="inst-hero-title">
                Deploy Structured <span className="highlight">Career Guidance</span> at Your Institution
              </h1>
              <p className="inst-hero-subtitle">
                Empower your students with data-driven career insights, comprehensive pathway mapping, and personalized guidance — all under your institution's brand.
              </p>
              <div className="inst-hero-cta">
                <a href="/partner" className="btn btn-primary">Request a Demo</a>
                <a href="https://12thplus.com/app/login" className="btn btn-secondary">Watch Overview</a>
              </div>
              <div className="inst-trust-badge">
                <div className="inst-trust-avatars">
                  <div className="avatar blue"></div>
                  <div className="avatar teal"></div>
                  <div className="avatar green"></div>
                  <div className="avatar yellow"></div>
                </div>
                <div className="inst-trust-text">
                  <strong>Trusted by 50+ Institutions</strong>
                  <span>Empowering 10,000+ students</span>
                </div>
              </div>
            </div>
            <div className="inst-hero-image">
              <img src="/institution_banner.png" alt="Institutions empowering students" />
            </div>
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section className="section inst-challenge-section">
        <div className="container">
          <h2 className="section-title">THE INSTITUTIONAL <span className="highlight">CHALLENGE</span></h2>
          <p className="section-subtitle">
            Most institutions struggle to provide consistent, reliable career guidance to all students
          </p>

          <div className="challenge-grid">
            <div className="challenge-card-pink">
              <div className="challenge-icon-pink">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </div>
              <h3>Time-Consuming</h3>
              <p>Counsellors spend hours on repetitive queries instead of impactful guidance</p>
            </div>

            <div className="challenge-card-pink">
              <div className="challenge-icon-pink">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              <h3>Inconsistent Information</h3>
              <p>Students receive varying advice depending on who they consult</p>
            </div>

            <div className="challenge-card-pink">
              <div className="challenge-icon-pink">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3>Scalability Issues</h3>
              <p>One-on-one counselling cannot scale with growing student numbers</p>
            </div>

            <div className="challenge-card-pink">
              <div className="challenge-icon-pink">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              <h3>Outdated Data</h3>
              <p>Career information quickly becomes obsolete in a changing landscape</p>
            </div>

            <div className="challenge-card-pink">
              <div className="challenge-icon-pink">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                  <line x1="9" y1="21" x2="9" y2="9" />
                </svg>
              </div>
              <h3>No Unified Platform</h3>
              <p>Lack of centralized system to track student career progress</p>
            </div>
          </div>
        </div>
      </section>

      {/* What 12thPlus Enables Section */}
      <section className="section inst-enables-section">
        <div className="container">
          <h2 className="section-title">WHAT 12THPLUS <span className="highlight">ENABLES</span></h2>
          <p className="section-subtitle">
            A complete career guidance infrastructure that works for your institution
          </p>

          <div className="enables-grid">
            <div className="enables-card">
              <div className="enables-icon teal">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              </div>
              <h3>White-Labelled Platform</h3>
              <p>Deploy under your institution's brand with custom domain and branding</p>
            </div>

            <div className="enables-card">
              <div className="enables-icon blue">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>
              <h3>Zero Operational Load</h3>
              <p>We handle all updates, maintenance, and content — you focus on education</p>
            </div>

            <div className="enables-card">
              <div className="enables-icon purple">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3>Trust & Student Safety</h3>
              <p>No third-party data sharing — student information stays secure</p>
            </div>

            <div className="enables-card">
              <div className="enables-icon orange">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 4 23 10 17 10" />
                  <polyline points="1 20 1 14 7 14" />
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                </svg>
              </div>
              <h3>Always Updated</h3>
              <p>Real-time updates on colleges, exams, deadlines, and career pathways</p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Platform Capabilities Section */}
      <section className="section inst-capabilities-section">
        <div className="container">
          <h2 className="section-title">CORE PLATFORM <span className="highlight">CAPABILITIES</span></h2>
          <p className="section-subtitle">
            Everything your institution needs for comprehensive career guidance
          </p>

          <div className="capabilities-grid">
            <div className="capability-card">
              <div className="capability-icon teal">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                </svg>
              </div>
              <h3>Career Library</h3>
              <p>100+ detailed career profiles with salary data, skills required, and growth paths</p>
            </div>

            <div className="capability-card">
              <div className="capability-icon blue">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h3>Eligibility Checker</h3>
              <p>Instant eligibility verification for courses, colleges, and entrance exams</p>
            </div>

            <div className="capability-card">
              <div className="capability-icon purple">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 20V10" />
                  <path d="M18 20V4" />
                  <path d="M6 20v-4" />
                </svg>
              </div>
              <h3>College Rankings</h3>
              <p>Comprehensive college database with rankings, fees, placements, and reviews</p>
            </div>

            <div className="capability-card">
              <div className="capability-icon orange">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <h3>Timeline Tracker</h3>
              <p>Important dates for applications, exams, and deadlines — never miss an opportunity</p>
            </div>

            <div className="capability-card">
              <div className="capability-icon green">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <h3>Parent Portal</h3>
              <p>Keep parents informed and engaged with dedicated access and reports</p>
            </div>

            <div className="capability-card">
              <div className="capability-icon pink">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="20" x2="18" y2="10" />
                  <line x1="12" y1="20" x2="12" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="14" />
                </svg>
              </div>
              <h3>Analytics Dashboard</h3>
              <p>Track student engagement, popular careers, and counselling effectiveness</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section inst-benefits-section">
        <div className="container">
          <h2 className="section-title">BENEFITS FOR YOUR <span className="highlight">INSTITUTION</span></h2>
          <p className="section-subtitle">
            Transform how your institution delivers career guidance
          </p>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon teal">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <h3>Save Counsellor Time</h3>
              <p>Reduce repetitive queries by 70% with self-service student access</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon blue">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h3>Scale Without Hiring</h3>
              <p>Serve thousands of students without additional counselling staff</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon purple">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <h3>Build Parent Trust</h3>
              <p>Demonstrate commitment to student futures with professional tools</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon orange">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              </div>
              <h3>Enhance Reputation</h3>
              <p>Position your institution as forward-thinking and student-centric</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon green">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              </div>
              <h3>Cost Effective</h3>
              <p>Fraction of the cost of building in-house counselling infrastructure</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon pink">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h3>Proven Results</h3>
              <p>Track record of improving student outcomes and satisfaction</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who Should Adopt Section */}
      <section className="section inst-adopt-section">
        <div className="container">
          <h2 className="section-title">WHO SHOULD <span className="highlight">ADOPT</span> 12THPLUS</h2>
          <p className="section-subtitle">
            Designed for institutions serious about student success
          </p>

          <div className="adopt-tabs">
            <button
              className={`adopt-tab ${activeTab === 'schools' ? 'active' : ''}`}
              onClick={() => setActiveTab('schools')}
            >
              Schools
            </button>
            <button
              className={`adopt-tab ${activeTab === 'colleges' ? 'active' : ''}`}
              onClick={() => setActiveTab('colleges')}
            >
              PU/Junior Colleges
            </button>
            <button
              className={`adopt-tab ${activeTab === 'coaching' ? 'active' : ''}`}
              onClick={() => setActiveTab('coaching')}
            >
              Coaching Institutions
            </button>
            <button
              className={`adopt-tab ${activeTab === 'trusts' ? 'active' : ''}`}
              onClick={() => setActiveTab('trusts')}
            >
              Educational Trusts
            </button>
          </div>

          <div className="adopt-content">
            <div className="adopt-content-card">
              <h3>{tabContent[activeTab].title}</h3>
              <ul className="adopt-features">
                {tabContent[activeTab].features.map((feature, index) => (
                  <li key={index}>
                    <span className="adopt-check">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>
              <a href="/partner" className="btn btn-primary">Schedule Demo</a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="inst-cta-section">
        <div className="container">
          <h2>Partner With <span className="highlight">12thPlus</span> Today</h2>
          <p>Join leading institutions in transforming career guidance for students</p>
          <div className="inst-cta-buttons">
            <a href="/partner" className="btn btn-primary btn-lg">Schedule a Demo</a>
            <a href="https://12thplus.com/app/login" className="btn btn-secondary btn-lg">Download Brochure</a>
          </div>
          <div className="cta-trust-badges">
            <div className="cta-badge">
              <span className="cta-badge-icon">🔒</span>
              <span>Data Secure</span>
            </div>
            <div className="cta-badge">
              <span className="cta-badge-icon">🎯</span>
              <span>Quick Setup</span>
            </div>
            <div className="cta-badge">
              <span className="cta-badge-icon">💬</span>
              <span>24/7 Support</span>
            </div>
            <div className="cta-badge">
              <span className="cta-badge-icon">✨</span>
              <span>No Lock-in</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InstitutionsPage;
